import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { ShoppingBag, DollarSign } from 'lucide-react';

interface ServiceOrder {
  id: string;
  user_id: string;
  product_name: string;
  product_description: string;
  amount: number;
  currency: string;
  stripe_payment_id: string;
  stripe_customer_id: string;
  status: string;
  notes: string;
  created_at: string;
  updated_at: string;
  user_email?: string;
  user_name?: string;
}

export function ServiceOrdersManagement() {
  const [orders, setOrders] = useState<ServiceOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<ServiceOrder | null>(null);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchOrders();
  }, []);

  async function fetchOrders() {
    try {
      const { data, error } = await supabase
        .from('service_orders')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const { data: allUsers } = await supabase.rpc('get_all_users_for_admin');
      const usersMap = new Map((allUsers || []).map((u: any) => [u.id, u]));

      const ordersWithUsers = (data || []).map((order) => {
        const userData = usersMap.get(order.user_id) as any;
        return {
          ...order,
          user_email: userData?.email || 'Unknown',
          user_name: userData?.first_name && userData?.last_name
            ? `${userData.first_name} ${userData.last_name}`
            : userData?.email || 'Unknown',
        };
      });

      setOrders(ordersWithUsers);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  }

  async function updateOrderStatus(orderId: string, status: string) {
    try {
      const { error } = await supabase
        .from('service_orders')
        .update({ status })
        .eq('id', orderId);

      if (error) throw error;
      await fetchOrders();
      if (selectedOrder?.id === orderId) {
        setSelectedOrder({ ...selectedOrder, status });
      }
    } catch (error) {
      console.error('Error updating order:', error);
    }
  }

  async function saveNotes() {
    if (!selectedOrder) return;

    try {
      const { error } = await supabase
        .from('service_orders')
        .update({ notes })
        .eq('id', selectedOrder.id);

      if (error) throw error;

      await fetchOrders();
      setSelectedOrder({ ...selectedOrder, notes });
      alert('Notes sauvegardées');
    } catch (error) {
      console.error('Error saving notes:', error);
      alert('Erreur lors de la sauvegarde des notes');
    }
  }

  function openOrderDetails(order: ServiceOrder) {
    setSelectedOrder(order);
    setNotes(order.notes || '');
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">Chargement des commandes...</div>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new':
        return 'bg-blue-100 text-blue-800';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'new':
        return 'Nouvelle';
      case 'processing':
        return 'En cours';
      case 'completed':
        return 'Terminée';
      case 'cancelled':
        return 'Annulée';
      default:
        return status;
    }
  };

  const newOrdersCount = orders.filter(o => o.status === 'new').length;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold text-gray-900">Commandes de services</h2>
          {newOrdersCount > 0 && (
            <span className="px-3 py-1 bg-blue-600 text-white text-sm font-semibold rounded-full">
              {newOrdersCount} nouvelle{newOrdersCount > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="text-sm text-gray-600">{orders.length} commande(s)</div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Produit</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Montant</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {orders.map((order) => (
              <tr key={order.id} className="hover:bg-gray-50">
                <td className="px-4 py-4">
                  <div className="flex items-center gap-2">
                    <ShoppingBag size={16} className="text-gray-400" />
                    <span className="text-sm font-medium text-gray-900">{order.product_name}</span>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="text-sm text-gray-900">{order.user_name}</div>
                  <div className="text-xs text-gray-500">{order.user_email}</div>
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center gap-1 text-sm font-medium text-gray-900">
                    <DollarSign size={14} />
                    {order.amount.toFixed(2)} {order.currency}
                  </div>
                </td>
                <td className="px-4 py-4">
                  <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                    {getStatusLabel(order.status)}
                  </span>
                </td>
                <td className="px-4 py-4 text-sm text-gray-600">
                  {new Date(order.created_at).toLocaleDateString('fr-FR')}
                </td>
                <td className="px-4 py-4">
                  <button
                    onClick={() => openOrderDetails(order)}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Voir détails
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">{selectedOrder.product_name}</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Commandé par {selectedOrder.user_name} le {new Date(selectedOrder.created_at).toLocaleString('fr-FR')}
                </p>
              </div>
              <button
                onClick={() => setSelectedOrder(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Informations de commande</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Montant:</span>
                    <span className="font-medium text-gray-900">{selectedOrder.amount.toFixed(2)} {selectedOrder.currency}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">ID paiement Stripe:</span>
                    <span className="font-mono text-xs text-gray-900">{selectedOrder.stripe_payment_id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Client Stripe:</span>
                    <span className="font-mono text-xs text-gray-900">{selectedOrder.stripe_customer_id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Email client:</span>
                    <span className="text-gray-900">{selectedOrder.user_email}</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Changer le statut</h4>
                <div className="flex gap-2">
                  <button
                    onClick={() => updateOrderStatus(selectedOrder.id, 'new')}
                    className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Nouvelle
                  </button>
                  <button
                    onClick={() => updateOrderStatus(selectedOrder.id, 'processing')}
                    className="px-3 py-1 text-xs bg-yellow-600 text-white rounded hover:bg-yellow-700"
                  >
                    En cours
                  </button>
                  <button
                    onClick={() => updateOrderStatus(selectedOrder.id, 'completed')}
                    className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Terminée
                  </button>
                  <button
                    onClick={() => updateOrderStatus(selectedOrder.id, 'cancelled')}
                    className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Annulée
                  </button>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Notes internes</h4>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Ajoutez des notes sur cette commande..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows={4}
                />
                <button
                  onClick={saveNotes}
                  className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Sauvegarder les notes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
